import { Button, Callout, Field, Input } from "@nodus/ui";
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { AuthCard } from "../../../app/auth-card";
import { useRuntime } from "../../../app/runtime";
import { normalizeUserCode } from "../../../features/console/cli-auth";
import { useCliOrgs } from "../../../features/console/cli-orgs";
import { OrgPicker } from "../../../features/console/org-picker";

// `nodus login --device` (RFC 8628, ADR-081): confirm the code shown in the terminal, pick orgs, approve or deny.
export const Route = createFileRoute("/_authed/cli-auth/device")({
  validateSearch: (s: Record<string, unknown>): { user_code?: string } => (typeof s.user_code === "string" ? { user_code: s.user_code } : {}),
  component: DeviceLogin,
});

function DeviceLogin() {
  const search = Route.useSearch();
  const { api } = useRuntime();
  const { orgs, loaded, selected, setChosen, gate, refresh } = useCliOrgs();
  const [code, setCode] = React.useState(normalizeUserCode(search.user_code ?? ""));
  const [state, setState] = React.useState<"ask" | "approved" | "denied">("ask");
  const [error, setError] = React.useState<string>();
  const complete = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(code);

  if (state === "approved") return <AuthCard title="Device approved" description="Return to your terminal; the CLI finishes signing in." />;
  if (state === "denied") return <AuthCard title="Request denied" description="The CLI on that device stays signed out." />;
  if (gate) return gate;

  async function decide(approve: boolean) {
    setError(undefined);
    try {
      if (approve) await api.approveDevice(code, selected);
      else await api.denyDevice(code);
      setState(approve ? "approved" : "denied");
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code did not work. Check it and try again.");
      void refresh();
    }
  }

  return (
    <AuthCard title="Sign in a device" description="Enter the code shown in your terminal.">
      <div className="grid gap-5">
        <Field id="user-code" label="Device code" required>
          <Input value={code} autoComplete="off" className="font-mono tracking-[0.2em] uppercase" onChange={(e) => setCode(normalizeUserCode(e.target.value))} />
        </Field>
        {orgs?.length ? (
          <OrgPicker orgs={orgs} chosen={selected} onChange={setChosen} />
        ) : loaded ? (
          <Callout tone="info"><a className="underline" href="/onboarding" target="_blank" rel="noopener noreferrer">Verify your card and create your organization</a>, then return here and refresh your organizations. If your device code expires, run nodus login --device again.
            <Button variant="outline" onClick={() => void refresh()}>Refresh organizations</Button></Callout>
        ) : null}
        {error ? <p role="alert" className="text-ui text-danger-fg">{error}</p> : null}
        <div className="flex gap-2">
          <Button disabled={!complete || !loaded || !orgs?.length || selected.length === 0} onClick={() => void decide(true)}>Approve</Button>
          <Button variant="outline" disabled={!complete} onClick={() => void decide(false)}>Deny</Button>
        </div>
      </div>
    </AuthCard>
  );
}
